// @tennis-matches/services/trainerstunden/billing-statements
// Service for managing persistent billing statements (parents/players)
//
// Doppelfaktur-Schutz: mehrere aktive Statements pro Empfänger/Monat sind erlaubt (Migration 2026_06_26…).
// Gleiche (session_id, person_id) bei kind='session' darf nur einmal unter aktiven Parent-Statements
// (draft/sent/partially_paid/paid) vorkommen — DB-Trigger + App-Logik.

import {
  areBillingStatementsInSameSplitTree,
  isBillingStatementDraftMergeBlocked,
} from "./billing-statement-draft-split.js";
import { billingRecipientsShareCarriedGroup } from "./billing-statement-recipient-carried.js";
import { getSupabaseClient } from "../supabase/index.js";
import {
  isHubReadScopeCurrent,
  registerHubCacheClear,
  resolveHubReadScope,
} from "../hub/hub-scoped-cache.js";
import { releaseCancelledBillingSessionItemsForNewDraft } from "./billing-cancelled-session-release.js";
import {
  billingStatementNeedsReissuePrep,
  isTrainerstundenBillingMissingColumnError,
  updateBillingStatementWithSchemaFallback,
} from "./billing-statement-db-compat.js";
import {
  freezeBillingStatementInvoiceDocument,
  snapshotBillingStatementDraftMasterData,
} from "./billing-statement-invoice-freeze.js";
import { buildBillingStatementPaymentReferenceForSend } from "./billing-statement-invoice-number.js";
import {
  computeBillingStatementPaymentDueDate,
  isoDateFromLocalTimestamp,
} from "./trainer-payment-due-days.js";

/** Postgres/Supabase-Fehler: doppelte aktive session/person-Position (siehe Trigger-Migration). */
export function isTrainerstundenBillingDuplicateSessionPersonError(err) {
  const raw =
    err && typeof err === "object" ? err.message || err.details || "" : err;
  const msg = String(raw ?? "").toLowerCase();
  return (
    msg.includes("trainerstunden_billing_duplicate_session_person_active") ||
    msg.includes("trainerstunden_billing_reactivate_statement_item_conflict")
  );
}

/** @param {unknown} n @returns {number} */
function round2(n) {
  return Math.round((Number(n) || 0) * 100) / 100;
}

/**
 * Aggregiert Postings zu gebuchten bzw. stornierten Korrekturen (booked/reversed; reversed inkl. Gegenpostings → netto).
 * @param {object[]} postingRows
 * @returns {{ correctionAmount: number, postingCount: number, byKind: { delta_item: number, adjustment_item: number, reversal_item: number } }}
 */
function aggregateBookedCorrectionPostingRows(postingRows) {
  const byKind = { delta_item: 0, adjustment_item: 0, reversal_item: 0 };
  let correctionAmount = 0;
  let postingCount = 0;
  const rows = Array.isArray(postingRows) ? postingRows : [];
  for (const p of rows) {
    const st =
      p?.correction_status != null ? String(p.correction_status).trim() : "";
    if (st && st !== "booked" && st !== "reversed") continue;
    const a = round2(p?.amount_eur);
    if (!Number.isFinite(a)) continue;
    correctionAmount = round2(correctionAmount + a);
    postingCount += 1;
    const k = String(p?.posting_kind || "").trim();
    if (k in byKind) byKind[k] = round2(byKind[k] + a);
  }
  return { correctionAmount, postingCount, byKind };
}

/**
 * Korrektur-Summe aus bereits geladenen Postings (ohne DB), nur statement_id.
 * @param {string} statementId
 * @param {object[]} postings
 * @returns {{ statementId: string, correctionAmount: number, postingCount: number, byKind: object }}
 */
export function buildBillingStatementCorrectionSummaryFromPostings(
  statementId,
  postings = [],
) {
  const sid = String(statementId ?? "").trim();
  const rows = (Array.isArray(postings) ? postings : []).filter(
    (p) => String(p?.statement_id || "").trim() === sid,
  );
  const agg = aggregateBookedCorrectionPostingRows(rows);
  return { statementId: sid, ...agg };
}

/**
 * Original-, Korrektur- und Effektivbeträge sowie optional korrigierter Zahlungsstand.
 * Keine Persistenz — nur Anzeige-/Kennzahlen.
 *
 * @param {object} statement — u. a. total_eur, paid_eur, status
 * @param {{ correctionAmount?: number }} summary — aus Postings aggregiert
 */
export function getBillingStatementDisplayTotalsForStatement(
  statement,
  summary,
) {
  const st = statement && typeof statement === "object" ? statement : {};
  const s = summary && typeof summary === "object" ? summary : {};
  const status = String(st.status || "").trim();
  const originalAmount = round2(st.total_eur);
  const correctionAmount = round2(s.correctionAmount);
  const effectiveAmount = round2(originalAmount + correctionAmount);
  const paidAmount = round2(st.paid_eur);
  const showPaymentLines =
    status !== "cancelled" &&
    ["sent", "partially_paid", "paid"].includes(status);
  const openAmountCorrected = round2(effectiveAmount - paidAmount);
  return {
    originalAmount,
    correctionAmount,
    effectiveAmount,
    paidAmount,
    openAmountCorrected,
    showPaymentLines,
  };
}

const BILLING_STATEMENTS_CACHE_MS = 15000;
/** @type {Map<string, Promise<{ok: boolean, data?: Array, error?: any}>>} */
const __billingStatementsInflight = new Map();
/** @type {Map<string, { ts: number, value: {ok: boolean, data?: Array, error?: any} }>} */
const __billingStatementsCache = new Map();

const HUB_BILLING_STATEMENTS_CACHE_MS = 15000;
/** @type {Map<string, Promise<{ok: boolean, data?: Array, error?: any}>>} */
const __hubBillingStatementsInflight = new Map();
/** @type {Map<string, { ts: number, value: {ok: boolean, data?: Array, error?: any} }>} */
const __hubBillingStatementsCache = new Map();

const HUB_BILLING_STATEMENT_SELECT = [
  "id",
  "status",
  "open_eur",
  "due_date",
  "period_month",
  "person_id",
  "billing_contact_id",
  "recipient_type",
  "recipient_name_snapshot",
  "total_eur",
  "paid_eur",
  "sent_at",
  "created_at",
].join(",");

export function clearBillingStatementsCache() {
  __billingStatementsCache.clear();
  __billingStatementsInflight.clear();
  __hubBillingStatementsCache.clear();
  __hubBillingStatementsInflight.clear();
}

registerHubCacheClear(clearBillingStatementsCache);

export async function loadBillingStatements(options = {}) {
  const force = options?.force === true;
  const scope = await resolveHubReadScope();
  const cacheKey = scope.key;
  const now = Date.now();
  if (!force) {
    const cached = __billingStatementsCache.get(cacheKey);
    if (cached && now - cached.ts < BILLING_STATEMENTS_CACHE_MS) {
      return cached.value;
    }
    const inflight = __billingStatementsInflight.get(cacheKey);
    if (inflight) return inflight;
  }

  const started = { ...scope };
  const run = (async () => {
    try {
      const supabase = await getSupabaseClient();
      if (!supabase) return { ok: false, error: "no-supabase" };

      const { data, error } = await supabase
        .from("trainerstunden_billing_statements")
        .select("*")
        .order("period_month", { ascending: false })
        .order("created_at", { ascending: false });
      if (error) {
        console.warn("[loadBillingStatements] Error:", error);
        return { ok: false, error };
      }
      return { ok: true, data: data || [] };
    } catch (e) {
      console.error("[loadBillingStatements] Exception:", e);
      return { ok: false, error: e };
    }
  })();

  const promise = run
    .then((value) => {
      if (value?.ok && isHubReadScopeCurrent(started)) {
        __billingStatementsCache.set(cacheKey, { ts: Date.now(), value });
      }
      return value;
    })
    .finally(() => {
      if (__billingStatementsInflight.get(cacheKey) === promise) {
        __billingStatementsInflight.delete(cacheKey);
      }
    });
  __billingStatementsInflight.set(cacheKey, promise);
  return promise;
}

/**
 * Hub-Attention / Overdue-Peek: nur offene Zahlungsstände, schlanke Spalten.
 * @param {{ force?: boolean }} [options]
 */
export async function loadBillingStatementsForHub(options = {}) {
  const force = options?.force === true;
  const scope = await resolveHubReadScope();
  const cacheKey = scope.key;
  const now = Date.now();
  if (!force) {
    const cached = __hubBillingStatementsCache.get(cacheKey);
    if (cached && now - cached.ts < HUB_BILLING_STATEMENTS_CACHE_MS) {
      return cached.value;
    }
    const inflight = __hubBillingStatementsInflight.get(cacheKey);
    if (inflight) return inflight;
  }

  const started = { ...scope };
  const run = (async () => {
    try {
      const supabase = await getSupabaseClient();
      if (!supabase) return { ok: false, error: "no-supabase" };

      const { data, error } = await supabase
        .from("trainerstunden_billing_statements")
        .select(HUB_BILLING_STATEMENT_SELECT)
        .in("status", ["sent", "partially_paid"])
        .order("period_month", { ascending: false })
        .order("created_at", { ascending: false });
      if (error) {
        console.warn("[loadBillingStatementsForHub] Error:", error);
        return { ok: false, error };
      }
      return { ok: true, data: data || [] };
    } catch (e) {
      console.error("[loadBillingStatementsForHub] Exception:", e);
      return { ok: false, error: e };
    }
  })();

  const promise = run
    .then((value) => {
      if (value?.ok && isHubReadScopeCurrent(started)) {
        __hubBillingStatementsCache.set(cacheKey, { ts: Date.now(), value });
      }
      return value;
    })
    .finally(() => {
      if (__hubBillingStatementsInflight.get(cacheKey) === promise) {
        __hubBillingStatementsInflight.delete(cacheKey);
      }
    });
  __hubBillingStatementsInflight.set(cacheKey, promise);
  return promise;
}

export async function loadBillingStatementItems(options = {}) {
  try {
    const supabase = await getSupabaseClient();
    if (!supabase) return { ok: false, error: "no-supabase" };

    let q = supabase.from("trainerstunden_billing_statement_items").select("*");
    if (options.statementId)
      q = q.eq("statement_id", String(options.statementId));
    q = q
      .order("date", { ascending: true })
      .order("created_at", { ascending: true });

    const { data, error } = await q;
    if (error) {
      console.warn("[loadBillingStatementItems] Error:", error);
      return { ok: false, error };
    }
    return { ok: true, data: data || [] };
  } catch (e) {
    console.error("[loadBillingStatementItems] Exception:", e);
    return { ok: false, error: e };
  }
}

export async function createBillingStatement(header, items) {
  try {
    const supabase = await getSupabaseClient();
    if (!supabase) return { ok: false, error: "no-supabase" };

    const h = header && typeof header === "object" ? header : {};
    const lines = Array.isArray(items) ? items : [];
    if (!h.id) return { ok: false, error: "missing-header-id" };

    let headerPayload = h;
    let { data: insertedHeader, error: hErr } = await supabase
      .from("trainerstunden_billing_statements")
      .insert([headerPayload])
      .select()
      .single();
    if (
      hErr &&
      isTrainerstundenBillingMissingColumnError(hErr) &&
      headerPayload.draft_issuer_trainer_id != null
    ) {
      const { draft_issuer_trainer_id: _drop, ...rest } = headerPayload;
      headerPayload = rest;
      ({ data: insertedHeader, error: hErr } = await supabase
        .from("trainerstunden_billing_statements")
        .insert([headerPayload])
        .select()
        .single());
    }
    if (hErr) {
      console.warn("[createBillingStatement] Header insert error:", hErr);
      return { ok: false, error: hErr };
    }

    const headerId = String(insertedHeader?.id || h.id || "").trim();

    let insertedItems = [];
    if (lines.length) {
      const { data: itemsData, error: iErr } = await supabase
        .from("trainerstunden_billing_statement_items")
        .insert(lines)
        .select();
      if (iErr) {
        console.warn("[createBillingStatement] Items insert error:", iErr);
        if (headerId) {
          const { error: delErr } = await supabase
            .from("trainerstunden_billing_statements")
            .delete()
            .eq("id", headerId);
          if (delErr)
            console.warn(
              "[createBillingStatement] Header rollback delete failed:",
              delErr,
            );
        }
        if (isTrainerstundenBillingDuplicateSessionPersonError(iErr)) {
          return {
            ok: false,
            error: "duplicate-session-person-active",
            details: iErr,
          };
        }
        return { ok: false, error: iErr };
      }
      insertedItems = Array.isArray(itemsData) ? itemsData : [];

      const release = await releaseCancelledBillingSessionItemsForNewDraft(
        supabase,
        {
          items: lines,
          excludeStatementId: headerId,
        },
      );
      if (!release.ok) {
        console.warn(
          "[createBillingStatement] release cancelled session items failed:",
          release.error,
        );
      }
    }

    let creditAppliedEur = 0;
    if (
      String(insertedHeader?.status || h.status || "").trim() === "draft" &&
      headerId
    ) {
      const snap = await snapshotBillingStatementDraftMasterData(
        supabase,
        headerId,
      );
      if (!snap.ok) {
        console.warn(
          "[createBillingStatement] draft master-data snapshot failed:",
          snap.reason || snap.error,
        );
      }
      const { applyAvailableBillingCreditToStatement } =
        await import("./billing-credit.js");
      const creditApply =
        await applyAvailableBillingCreditToStatement(headerId);
      if (!creditApply.ok) {
        console.warn(
          "[createBillingStatement] credit apply failed:",
          creditApply.reason || creditApply.error,
        );
      } else {
        creditAppliedEur = round2(Number(creditApply.appliedEur) || 0);
        if (creditAppliedEur > 0.004) {
          await updateBillingStatementPaymentStatus(headerId);
          const { data: refreshed } = await supabase
            .from("trainerstunden_billing_statements")
            .select("*")
            .eq("id", headerId)
            .single();
          if (refreshed) insertedHeader = refreshed;
        }
      }
    }

    clearBillingStatementsCache();
    return {
      ok: true,
      data: insertedHeader,
      items: insertedItems,
      creditAppliedEur,
    };
  } catch (e) {
    console.error("[createBillingStatement] Exception:", e);
    return { ok: false, error: e };
  }
}

export async function createBillingStatementsForMonth(
  periodMonth,
  drafts = [],
) {
  try {
    const pm = String(periodMonth || "").trim();
    if (!pm) return { ok: false, error: "missing-period-month" };
    const list = Array.isArray(drafts) ? drafts : [];
    const created = [];
    for (const d of list) {
      const r = await createBillingStatement(d?.header, d?.items);
      if (!r?.ok)
        return { ok: false, error: r.error || "create-failed", created };
      created.push(r.data);
    }
    return { ok: true, data: created };
  } catch (e) {
    console.error("[createBillingStatementsForMonth] Exception:", e);
    return { ok: false, error: e };
  }
}

/**
 * Manuelle Positionen zu einem Entwurf hinzufügen und Summen aktualisieren.
 *
 * @param {string} statementId
 * @param {object[]} items
 * @param {number} amountDelta
 */
export async function appendBillingStatementDraftLines(
  statementId,
  items,
  amountDelta,
) {
  try {
    const supabase = await getSupabaseClient();
    if (!supabase) return { ok: false, error: "no-supabase" };
    const id = String(statementId || "").trim();
    if (!id) return { ok: false, error: "missing-id" };

    const lines = Array.isArray(items) ? items : [];
    const delta = round2(amountDelta);
    if (!lines.length || !(delta > 0.004))
      return { ok: false, error: "no-lines" };

    const { data: st, error: stErr } = await supabase
      .from("trainerstunden_billing_statements")
      .select("id,status,total_eur,open_eur")
      .eq("id", id)
      .single();
    if (stErr) return { ok: false, error: stErr };
    if (String(st?.status || "").trim() !== "draft")
      return { ok: false, error: "not-draft" };

    const { data: insertedItems, error: iErr } = await supabase
      .from("trainerstunden_billing_statement_items")
      .insert(lines)
      .select();
    if (iErr) return { ok: false, error: iErr };

    const newTotal = round2((Number(st.total_eur) || 0) + delta);
    const newOpen = round2((Number(st.open_eur) || 0) + delta);
    const { data, error: uErr } = await supabase
      .from("trainerstunden_billing_statements")
      .update({ total_eur: newTotal, open_eur: newOpen })
      .eq("id", id)
      .select()
      .single();
    if (uErr) return { ok: false, error: uErr };
    const snap = await snapshotBillingStatementDraftMasterData(supabase, id);
    if (!snap.ok) {
      console.warn(
        "[appendBillingStatementDraftLines] draft master-data snapshot failed:",
        snap.reason || snap.error,
      );
    }
    return {
      ok: true,
      data,
      items: Array.isArray(insertedItems) ? insertedItems : [],
    };
  } catch (e) {
    return { ok: false, error: e };
  }
}

/**
 * Items aus einem Entwurf zurücknehmen.
 * - Nur erlaubt, solange das Statement im Status 'draft' ist.
 * - Aktualisiert total_eur/open_eur entsprechend.
 * - Wenn dadurch alle Positionen entfernt werden, wird der leere Entwurf gelöscht.
 * - `options.permanent`: Session|Person wird ausgeschlossen und nicht erneut gebündelt.
 *
 * @param {string} statementId
 * @param {string[]} itemIds
 * @param {{ permanent?: boolean }} [options]
 * @returns {Promise<{ ok: true, removed: number, deletedStatement: boolean, data?: object|null, exclusions?: object[] } | { ok: false, error: any }>}
 */
export async function removeBillingStatementDraftLines(
  statementId,
  itemIds,
  options = {},
) {
  try {
    const supabase = await getSupabaseClient();
    if (!supabase) return { ok: false, error: "no-supabase" };
    const id = String(statementId || "").trim();
    if (!id) return { ok: false, error: "missing-id" };
    const permanent = options?.permanent === true;

    const ids = [
      ...new Set(
        (Array.isArray(itemIds) ? itemIds : [])
          .map((x) => String(x || "").trim())
          .filter(Boolean),
      ),
    ];
    if (!ids.length) return { ok: false, error: "no-items" };

    const { data: st, error: stErr } = await supabase
      .from("trainerstunden_billing_statements")
      .select("id,status,total_eur,open_eur,tenant_id,created_by")
      .eq("id", id)
      .single();
    if (stErr) return { ok: false, error: stErr };
    if (String(st?.status || "").trim() !== "draft")
      return { ok: false, error: "not-draft" };

    const { data: toRemove, error: selErr } = await supabase
      .from("trainerstunden_billing_statement_items")
      .select("id,amount_eur,kind,session_id,person_id")
      .eq("statement_id", id)
      .in("id", ids);
    if (selErr) return { ok: false, error: selErr };
    const removeIds = (toRemove || [])
      .map((it) => String(it.id))
      .filter(Boolean);
    if (!removeIds.length)
      return { ok: false, error: "items-not-in-statement" };

    const removedSum = (toRemove || []).reduce(
      (s, it) => s + (Number(it.amount_eur) || 0),
      0,
    );

    let exclusions = [];
    if (permanent) {
      const { upsertBillingSessionPersonExclusions } =
        await import("./billing-session-person-exclusions.js");
      const pairs = (toRemove || [])
        .filter((it) => String(it?.kind || "").trim() === "session")
        .map((it) => ({
          session_id: String(it?.session_id || "").trim(),
          person_id: String(it?.person_id || "").trim(),
        }))
        .filter((p) => p.session_id && p.person_id);
      if (pairs.length) {
        const { data: authData } = await supabase.auth.getUser();
        const createdBy = String(authData?.user?.id || "").trim();
        if (!createdBy) return { ok: false, error: "missing-created-by" };
        const ex = await upsertBillingSessionPersonExclusions({
          tenantId: st?.tenant_id || null,
          createdBy,
          sourceStatementId: id,
          pairs,
        });
        if (!ex.ok) return { ok: false, error: ex.error || "exclusion-failed" };
        exclusions = Array.isArray(ex.data) ? ex.data : [];
      }
    }

    const { error: delErr } = await supabase
      .from("trainerstunden_billing_statement_items")
      .delete()
      .eq("statement_id", id)
      .in("id", removeIds);
    if (delErr) return { ok: false, error: delErr };

    const { data: remaining, error: remErr } = await supabase
      .from("trainerstunden_billing_statement_items")
      .select("id", { count: "exact", head: false })
      .eq("statement_id", id);
    if (remErr) return { ok: false, error: remErr };

    if (!Array.isArray(remaining) || remaining.length === 0) {
      const { error: delStErr } = await supabase
        .from("trainerstunden_billing_statements")
        .delete()
        .eq("id", id)
        .eq("status", "draft");
      if (delStErr) return { ok: false, error: delStErr };
      return {
        ok: true,
        removed: removeIds.length,
        deletedStatement: true,
        data: null,
        exclusions,
      };
    }

    const newTotal = round2((Number(st.total_eur) || 0) - removedSum);
    const newOpen = round2((Number(st.open_eur) || 0) - removedSum);
    const safeTotal = newTotal < 0 ? 0 : newTotal;
    const safeOpen = newOpen < 0 ? 0 : newOpen;
    const { data, error: uErr } = await supabase
      .from("trainerstunden_billing_statements")
      .update({ total_eur: safeTotal, open_eur: safeOpen })
      .eq("id", id)
      .select()
      .single();
    if (uErr) return { ok: false, error: uErr };
    return {
      ok: true,
      removed: removeIds.length,
      deletedStatement: false,
      data,
      exclusions,
    };
  } catch (e) {
    return { ok: false, error: e };
  }
}

/**
 * Entwurf vollständig löschen (alle Positionen entfernen, Statement löschen).
 * Mit `permanent: true` werden Session|Person-Paare ausgeschlossen und erscheinen
 * nicht wieder als offene Leistung / neuer Entwurf („Nicht berechnen“).
 *
 * @param {string} statementId
 * @param {{ permanent?: boolean }} [options]
 * @returns {Promise<{ ok: true, removed: number, deletedStatement: boolean, data?: object|null, exclusions?: object[] } | { ok: false, error: any }>}
 */
export async function deleteBillingStatementDraft(statementId, options = {}) {
  try {
    const supabase = await getSupabaseClient();
    if (!supabase) return { ok: false, error: "no-supabase" };
    const id = String(statementId || "").trim();
    if (!id) return { ok: false, error: "missing-id" };
    const permanent = options?.permanent === true;

    const { data: st, error: stErr } = await supabase
      .from("trainerstunden_billing_statements")
      .select("id,status")
      .eq("id", id)
      .single();
    if (stErr) return { ok: false, error: stErr };
    if (String(st?.status || "").trim() !== "draft")
      return { ok: false, error: "not-draft" };

    const { data: items, error: itemsErr } = await supabase
      .from("trainerstunden_billing_statement_items")
      .select("id")
      .eq("statement_id", id);
    if (itemsErr) return { ok: false, error: itemsErr };

    const itemIds = (items || [])
      .map((it) => String(it?.id || "").trim())
      .filter(Boolean);
    if (itemIds.length) {
      return removeBillingStatementDraftLines(id, itemIds, { permanent });
    }

    const { error: delErr } = await supabase
      .from("trainerstunden_billing_statements")
      .delete()
      .eq("id", id)
      .eq("status", "draft");
    if (delErr) return { ok: false, error: delErr };
    return {
      ok: true,
      removed: 0,
      deletedStatement: true,
      data: null,
    };
  } catch (e) {
    return { ok: false, error: e };
  }
}

/**
 * Einzelne Entwurfsposition anpassen (Datum, Beschreibung, Betrag).
 *
 * @param {string} statementId
 * @param {string} itemId
 * @param {{ date?: string, description?: string, amount_eur?: number, duration_min?: number }} patch
 */
export async function updateBillingStatementDraftLine(
  statementId,
  itemId,
  patch = {},
) {
  try {
    const supabase = await getSupabaseClient();
    if (!supabase) return { ok: false, error: "no-supabase" };

    const sid = String(statementId || "").trim();
    const iid = String(itemId || "").trim();
    if (!sid || !iid) return { ok: false, error: "missing-id" };

    const { data: st, error: stErr } = await supabase
      .from("trainerstunden_billing_statements")
      .select("id,status")
      .eq("id", sid)
      .single();
    if (stErr) return { ok: false, error: stErr };
    if (String(st?.status || "").trim() !== "draft")
      return { ok: false, error: "not-draft" };

    const { data: existing, error: itemErr } = await supabase
      .from("trainerstunden_billing_statement_items")
      .select("id, pricing_snapshot")
      .eq("id", iid)
      .eq("statement_id", sid)
      .maybeSingle();
    if (itemErr) return { ok: false, error: itemErr };
    if (!existing) return { ok: false, error: "item-not-found" };

    const itemPatch = {};
    let pricingSnapshot =
      existing.pricing_snapshot && typeof existing.pricing_snapshot === "object"
        ? { ...existing.pricing_snapshot }
        : null;

    if (patch.date !== undefined) {
      const d = String(patch.date || "")
        .trim()
        .slice(0, 10);
      if (!/^\d{4}-\d{2}-\d{2}$/.test(d))
        return { ok: false, error: "invalid-date" };
      itemPatch.date = d;
    }
    if (patch.description !== undefined) {
      itemPatch.description = String(patch.description ?? "").trim() || null;
    }
    if (patch.duration_min !== undefined) {
      const dur = Math.floor(Number(patch.duration_min));
      if (!Number.isFinite(dur) || dur < 1)
        return { ok: false, error: "invalid-duration" };
      pricingSnapshot = { ...(pricingSnapshot || {}), duration_min: dur };
    }
    if (patch.amount_eur !== undefined) {
      const amount = round2(Number(patch.amount_eur));
      if (!Number.isFinite(amount) || amount < 0)
        return { ok: false, error: "invalid-amount" };
      itemPatch.amount_eur = amount;
      pricingSnapshot = { ...(pricingSnapshot || {}), share_eur: amount };
    }
    if (
      (patch.duration_min !== undefined || patch.amount_eur !== undefined) &&
      pricingSnapshot
    ) {
      itemPatch.pricing_snapshot = pricingSnapshot;
    }
    if (!Object.keys(itemPatch).length) return { ok: false, error: "no-patch" };

    itemPatch.line_title_snapshot = null;

    const { error: updErr } = await supabase
      .from("trainerstunden_billing_statement_items")
      .update(itemPatch)
      .eq("id", iid)
      .eq("statement_id", sid);
    if (updErr) return { ok: false, error: updErr };

    const { data: allItems, error: allErr } = await supabase
      .from("trainerstunden_billing_statement_items")
      .select("amount_eur")
      .eq("statement_id", sid);
    if (allErr) return { ok: false, error: allErr };

    const newTotal = round2(
      (Array.isArray(allItems) ? allItems : []).reduce(
        (sum, it) => sum + (Number(it?.amount_eur) || 0),
        0,
      ),
    );
    const { data: updated, error: stUpdErr } = await supabase
      .from("trainerstunden_billing_statements")
      .update({
        total_eur: newTotal,
        open_eur: newTotal,
        payment_reference: null,
      })
      .eq("id", sid)
      .eq("status", "draft")
      .select()
      .single();
    if (stUpdErr) return { ok: false, error: stUpdErr };

    return { ok: true, data: updated, itemPatch };
  } catch (e) {
    console.error("[updateBillingStatementDraftLine] Exception:", e);
    return { ok: false, error: e };
  }
}

/**
 * Zahlungsziel (Fälligkeitsdatum) für Entwürfe setzen.
 *
 * @param {string[]|string} statementIds
 * @param {string} dueDateIso YYYY-MM-DD
 */
export async function updateBillingStatementDraftDueDate(
  statementIds,
  dueDateIso,
) {
  try {
    const supabase = await getSupabaseClient();
    if (!supabase) return { ok: false, error: "no-supabase" };

    const due = String(dueDateIso || "")
      .trim()
      .slice(0, 10);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(due))
      return { ok: false, error: "invalid-due-date" };

    const ids = (Array.isArray(statementIds) ? statementIds : [statementIds])
      .map((id) => String(id || "").trim())
      .filter(Boolean);
    if (!ids.length) return { ok: false, error: "missing-ids" };

    const updated = [];
    for (const id of ids) {
      const { data, error } = await supabase
        .from("trainerstunden_billing_statements")
        .update({ due_date: due })
        .eq("id", id)
        .eq("status", "draft")
        .select()
        .single();
      if (error) return { ok: false, error, statementId: id };
      if (data) updated.push(data);
    }
    return { ok: true, data: updated };
  } catch (e) {
    console.error("[updateBillingStatementDraftDueDate] Exception:", e);
    return { ok: false, error: e };
  }
}

/**
 * Vor erneutem Versand: alte Rechnungsnummer und Dokument-Snapshots zurücksetzen.
 *
 * @param {import('@supabase/supabase-js').SupabaseClient} supabase
 * @param {string} statementId
 */
export async function prepareBillingStatementForResend(supabase, statementId) {
  try {
    const sb = supabase;
    const id = String(statementId || "").trim();
    if (!sb || !id) return { ok: false, reason: "missing-input" };

    const { data: st, error: stErr } = await sb
      .from("trainerstunden_billing_statements")
      .select(
        "id,status,total_eur,sent_at,payment_reference,draft_issuer_trainer_id,issuer_trainer_id_snapshot",
      )
      .eq("id", id)
      .single();
    if (stErr) return { ok: false, error: stErr };

    if (!billingStatementNeedsReissuePrep(st))
      return { ok: true, data: st, skipped: true };

    // C1: Aussteller aus Snapshot/Draft erhalten — kein stiller Verlust bei Revert/Resend-Prep.
    const preservedDraftIssuer =
      String(st?.draft_issuer_trainer_id ?? "").trim() ||
      String(st?.issuer_trainer_id_snapshot ?? "").trim() ||
      null;

    const { data: postingRows, error: postErr } = await sb
      .from("trainerstunden_billing_correction_postings")
      .select("*")
      .eq("statement_id", id);
    if (postErr) return { ok: false, error: postErr };

    const corrSummary = buildBillingStatementCorrectionSummaryFromPostings(
      id,
      postingRows || [],
    );
    const totals = getBillingStatementDisplayTotalsForStatement(
      { ...st, paid_eur: 0, status: "draft" },
      corrSummary,
    );
    const openRaw = Number(totals.openAmountCorrected);
    const open = Number.isFinite(openRaw) && openRaw < 0 ? 0 : round2(openRaw);

    const { error: itemsErr } = await sb
      .from("trainerstunden_billing_statement_items")
      .update({ line_title_snapshot: null, person_name_snapshot: null })
      .eq("statement_id", id);
    if (itemsErr && !isTrainerstundenBillingMissingColumnError(itemsErr)) {
      return { ok: false, error: itemsErr };
    }

    const corePatch = {
      status: "draft",
      payment_reference: null,
      sent_at: null,
      channel: null,
      paid_eur: 0,
      open_eur: open,
      invoice_mail_sent_at: null,
      invoice_mail_sent_to: null,
      invoice_mail_count: 0,
      payment_status: null,
      payment_token: null,
    };
    const fullPatch = {
      ...corePatch,
      invoice_frozen_at: null,
      recipient_address_snapshot: null,
      issuer_name_snapshot: null,
      issuer_address_snapshot: null,
      issuer_email_snapshot: null,
      issuer_trainer_id_snapshot: null,
      issuer_vat_mode_snapshot: null,
      issuer_vat_rate_snapshot: null,
      invoice_net_total_eur: null,
      invoice_vat_amount_eur: null,
      invoice_payment_snapshot: null,
      ...(preservedDraftIssuer
        ? { draft_issuer_trainer_id: preservedDraftIssuer }
        : {}),
    };

    const upd = await updateBillingStatementWithSchemaFallback(
      sb,
      id,
      fullPatch,
      corePatch,
    );
    if (!upd.ok) {
      if (isTrainerstundenBillingDuplicateSessionPersonError(upd.error)) {
        return {
          ok: false,
          reason: "reactivate-item-conflict",
          error: upd.error,
        };
      }
      return { ok: false, error: upd.error };
    }
    return { ok: true, data: upd.data, reissued: true };
  } catch (e) {
    return { ok: false, error: e };
  }
}

export async function markBillingStatementSent(
  statementId,
  channel,
  sentAt = null,
) {
  try {
    const supabase = await getSupabaseClient();
    if (!supabase) return { ok: false, error: "no-supabase" };
    const id = String(statementId || "").trim();
    if (!id) return { ok: false, error: "missing-id" };
    const nowIso = sentAt || new Date().toISOString();
    const ch = channel != null ? String(channel).trim() : null;

    const prep = await prepareBillingStatementForResend(supabase, id);
    if (!prep.ok) {
      return {
        ok: false,
        reason: prep.reason || "reissue-prep-failed",
        error: prep.error || prep.reason || "reissue-prep-failed",
      };
    }

    const { data: cur, error: curErr } = await supabase
      .from("trainerstunden_billing_statements")
      .select("id,period_month,payment_reference,status,due_date")
      .eq("id", id)
      .single();
    if (curErr) return { ok: false, error: curErr };

    const payment_reference =
      String(cur?.payment_reference ?? "").trim() ||
      buildBillingStatementPaymentReferenceForSend(cur);

    const frozen = await freezeBillingStatementInvoiceDocument(supabase, id);
    if (!frozen.ok) {
      return {
        ok: false,
        reason: frozen.reason || "freeze-failed",
        error: frozen.error || frozen.reason || "freeze-failed",
      };
    }

    const frozenSt =
      frozen.data && typeof frozen.data === "object" ? frozen.data : cur;
    const issuerTrainerId = String(
      frozenSt?.issuer_trainer_id_snapshot ??
        frozenSt?.draft_issuer_trainer_id ??
        "",
    ).trim();
    let issuerTrainer = null;
    if (issuerTrainerId) {
      const trR = await supabase
        .from("trainerstunden_trainers")
        .select("payment_due_days")
        .eq("id", issuerTrainerId)
        .maybeSingle();
      if (!trR.error && trR.data) issuerTrainer = trR.data;
    }
    const invoiceDateIso = isoDateFromLocalTimestamp(nowIso);
    const due_date =
      computeBillingStatementPaymentDueDate({
        invoiceDateIso,
        issuerTrainer,
      }) || null;

    const sendPatch = {
      status: "sent",
      sent_at: nowIso,
      channel: ch,
      payment_reference,
    };
    if (due_date) sendPatch.due_date = due_date;

    const { data, error } = await supabase
      .from("trainerstunden_billing_statements")
      .update(sendPatch)
      .eq("id", id)
      .select()
      .single();
    if (error) return { ok: false, error };
    return { ok: true, data };
  } catch (e) {
    return { ok: false, error: e };
  }
}

export async function markBillingStatementPaid(statementId) {
  try {
    const supabase = await getSupabaseClient();
    if (!supabase) return { ok: false, error: "no-supabase" };
    const id = String(statementId || "").trim();
    if (!id) return { ok: false, error: "missing-id" };

    const { data: st, error: rErr } = await supabase
      .from("trainerstunden_billing_statements")
      .select("id,total_eur")
      .eq("id", id)
      .single();
    if (rErr) return { ok: false, error: rErr };
    const total = Number(st?.total_eur) || 0;

    const { data, error } = await supabase
      .from("trainerstunden_billing_statements")
      .update({ status: "paid", paid_eur: total, open_eur: 0 })
      .eq("id", id)
      .select()
      .single();
    if (error) return { ok: false, error };
    return { ok: true, data };
  } catch (e) {
    return { ok: false, error: e };
  }
}

/**
 * PayPal-bestätigte Rechnungszahlung (nicht manuell rücknehmbar).
 * @param {object|undefined|null} statement
 */
export function isBillingStatementPaypalSettled(statement) {
  const st = statement && typeof statement === "object" ? statement : {};
  const method = String(st.payment_method ?? st.paymentMethod ?? "")
    .trim()
    .toLowerCase();
  if (method === "paypal") return true;
  const portalStatus = String(st.payment_status ?? st.paymentStatus ?? "")
    .trim()
    .toLowerCase();
  const paymentRef = String(
    st.payment_reference ?? st.paymentReference ?? "",
  ).trim();
  return portalStatus === "paid" && paymentRef !== "";
}

/**
 * @param {object|undefined|null} statement
 * @param {{ openAmountCorrected?: number, paidAmount?: number }} [displayTotals]
 */
export function canRevertManualBillingStatementPayment(
  statement,
  displayTotals = {},
) {
  const st = statement && typeof statement === "object" ? statement : {};
  if (String(st.status || "").trim() === "cancelled") return false;
  if (isBillingStatementPaypalSettled(st)) return false;

  const open = Number(displayTotals.openAmountCorrected);
  const paid = Number(displayTotals.paidAmount);
  const status = String(st.status || "").trim();
  if (status === "paid") return true;
  if (Number.isFinite(open) && open <= 0.004 && paid > 0.004) return true;
  return false;
}

/**
 * Manuelle Zahlungsmarkierung zurücknehmen: verbuchte Zahlungseingänge löschen,
 * Saldo neu berechnen. PayPal-bestätigte Zahlungen sind ausgeschlossen.
 *
 * @param {string} statementId
 */
export async function revertManualBillingStatementPayment(statementId) {
  try {
    const supabase = await getSupabaseClient();
    if (!supabase) return { ok: false, error: "no-supabase" };
    const id = String(statementId || "").trim();
    if (!id) return { ok: false, error: "missing-id" };

    const { data: st, error: stErr } = await supabase
      .from("trainerstunden_billing_statements")
      .select(
        "id,status,payment_method,payment_status,payment_reference,paid_at",
      )
      .eq("id", id)
      .single();
    if (stErr) return { ok: false, error: stErr };
    if (isBillingStatementPaypalSettled(st)) {
      return {
        ok: false,
        error: {
          code: "paypal-not-reversible",
          message: "paypal-payment-not-reversible",
        },
      };
    }

    const { data: paysBeforeDelete, error: paysLoadErr } = await supabase
      .from("trainerstunden_payments")
      .select("id")
      .eq("statement_id", id);
    if (paysLoadErr) return { ok: false, error: paysLoadErr };

    const { reverseBillingCreditForDeletedPayment } =
      await import("./billing-credit.js");
    for (const p of paysBeforeDelete || []) {
      await reverseBillingCreditForDeletedPayment(String(p?.id || "").trim());
    }

    const { error: payDelErr } = await supabase
      .from("trainerstunden_payments")
      .delete()
      .eq("statement_id", id);
    if (payDelErr) return { ok: false, error: payDelErr };

    const statusRes = await updateBillingStatementPaymentStatus(id);
    if (!statusRes.ok) return statusRes;

    const portalStatus = String(st.payment_status || "")
      .trim()
      .toLowerCase();
    const clearPatch = {
      paid_at: null,
      payment_method: null,
      payment_reference: null,
    };
    if (portalStatus === "paid") {
      clearPatch.payment_status = "confirmed_unpaid";
    }

    const { data, error: clearErr } = await supabase
      .from("trainerstunden_billing_statements")
      .update(clearPatch)
      .eq("id", id)
      .select()
      .single();
    if (clearErr) return { ok: false, error: clearErr };
    return { ok: true, data: data || statusRes.data };
  } catch (e) {
    return { ok: false, error: e };
  }
}

export async function cancelBillingStatement(statementId) {
  try {
    const supabase = await getSupabaseClient();
    if (!supabase) return { ok: false, error: "no-supabase" };
    const id = String(statementId || "").trim();
    if (!id) return { ok: false, error: "missing-id" };
    const { data, error } = await supabase
      .from("trainerstunden_billing_statements")
      .update({ status: "cancelled", open_eur: 0 })
      .eq("id", id)
      .select()
      .single();
    if (error) return { ok: false, error };
    return { ok: true, data };
  } catch (e) {
    return { ok: false, error: e };
  }
}

/** Status, die per Storno wieder in die Entwurfs-Prüfung (Rechnungen) zurückgelegt werden. */
const REVERT_INVOICED_TO_DRAFT_STATUSES = new Set([
  "sent",
  "partially_paid",
  "paid",
  "finalized",
]);

/**
 * Versandte/bezahlte Rechnung zurück in den Entwurf — für erneute Prüfung unter „Rechnungen“.
 * Entfernt zugeordnete Zahlungen und setzt Versand-/Zahlungsfelder zurück.
 *
 * @param {string} statementId
 */
export async function revertBillingStatementToDraft(statementId) {
  try {
    const supabase = await getSupabaseClient();
    if (!supabase) return { ok: false, error: "no-supabase" };
    const id = String(statementId || "").trim();
    if (!id) return { ok: false, error: "missing-id" };

    const { data: st, error: stErr } = await supabase
      .from("trainerstunden_billing_statements")
      .select("id,status,total_eur,sent_at")
      .eq("id", id)
      .single();
    if (stErr) return { ok: false, error: stErr };

    const status = String(st?.status || "").trim();
    const wasInvoiced =
      REVERT_INVOICED_TO_DRAFT_STATUSES.has(status) ||
      (status === "cancelled" &&
        st?.sent_at != null &&
        String(st.sent_at).trim() !== "");
    if (!wasInvoiced) {
      return {
        ok: false,
        error: {
          code: "invalid-status",
          message: "statement-not-revertible-to-draft",
        },
      };
    }

    const { data: paysBeforeDelete, error: paysLoadErr } = await supabase
      .from("trainerstunden_payments")
      .select("id")
      .eq("statement_id", id);
    if (paysLoadErr) return { ok: false, error: paysLoadErr };

    const { reverseBillingCreditForDeletedPayment } =
      await import("./billing-credit.js");
    for (const p of paysBeforeDelete || []) {
      await reverseBillingCreditForDeletedPayment(String(p?.id || "").trim());
    }

    const { error: payDelErr } = await supabase
      .from("trainerstunden_payments")
      .delete()
      .eq("statement_id", id);
    if (payDelErr) return { ok: false, error: payDelErr };

    const prep = await prepareBillingStatementForResend(supabase, id);
    if (!prep.ok)
      return {
        ok: false,
        error: prep.error || prep.reason || "reissue-prep-failed",
      };
    return { ok: true, data: prep.data };
  } catch (e) {
    return { ok: false, error: e };
  }
}

export async function updateBillingStatementPaymentStatusWithClient(
  supabase,
  statementId,
) {
  if (!supabase) return { ok: false, error: "no-supabase" };
  const id = String(statementId || "").trim();
  if (!id) return { ok: false, error: "missing-id" };

  const { data: st, error: stErr } = await supabase
    .from("trainerstunden_billing_statements")
    .select("id,status,total_eur")
    .eq("id", id)
    .single();
  if (stErr) return { ok: false, error: stErr };
  const status = String(st?.status || "");
  if (status === "cancelled") return { ok: true, data: st };

  const { data: pays, error: pErr } = await supabase
    .from("trainerstunden_payments")
    .select("amount_eur")
    .eq("statement_id", id);
  if (pErr) return { ok: false, error: pErr };
  const paid = (pays || []).reduce(
    (s, p) => s + (Number(p.amount_eur) || 0),
    0,
  );
  const paidRounded = round2(paid);

  const { data: postingRows, error: postErr } = await supabase
    .from("trainerstunden_billing_correction_postings")
    .select("*")
    .eq("statement_id", id);
  if (postErr) return { ok: false, error: postErr };

  const corrSummary = buildBillingStatementCorrectionSummaryFromPostings(
    id,
    postingRows || [],
  );
  const syntheticSt = { ...st, paid_eur: paidRounded };
  const totals = getBillingStatementDisplayTotalsForStatement(
    syntheticSt,
    corrSummary,
  );
  const openRaw = Number(totals.openAmountCorrected);
  const open = Number.isFinite(openRaw) && openRaw < 0 ? 0 : round2(openRaw);
  const eps = 0.004;

  const nextStatus =
    open <= eps
      ? "paid"
      : paidRounded > eps
        ? "partially_paid"
        : status === "sent" || status === "draft"
          ? status
          : "sent";

  const { data, error } = await supabase
    .from("trainerstunden_billing_statements")
    .update({ status: nextStatus, paid_eur: paidRounded, open_eur: open })
    .eq("id", id)
    .select()
    .single();
  if (error) return { ok: false, error };
  return { ok: true, data };
}

export async function updateBillingStatementPaymentStatus(statementId) {
  try {
    const supabase = await getSupabaseClient();
    const result = await updateBillingStatementPaymentStatusWithClient(
      supabase,
      statementId,
    );
    if (result?.ok) clearBillingStatementsCache();
    return result;
  } catch (e) {
    return { ok: false, error: e };
  }
}

/** PostgREST: sehr große `.in()`-Listen werden langsam oder brechen (URL/Parser); Chunking hält CoachDesk-Ladevorgang stabil. */
const BILLING_POSTINGS_IN_CHUNK = 100;

/**
 * Liest gebuchte Korrektur-Postings für die angegebenen Rechnungs-IDs (statement_id).
 * Reichert mit reason_code, session_id, booked_at und booked_by der zugehörigen Korrektur an (nur lesend).
 *
 * @param {string[]} statementIds
 * @returns {Promise<{ ok: boolean, data?: object[], error?: unknown }>}
 */
export async function loadBillingCorrectionPostingsForStatements(
  statementIds = [],
) {
  try {
    const supabase = await getSupabaseClient();
    if (!supabase) return { ok: false, error: "no-supabase" };

    const ids = [
      ...new Set(
        (Array.isArray(statementIds) ? statementIds : [])
          .map((x) => String(x || "").trim())
          .filter(Boolean),
      ),
    ];
    if (!ids.length) return { ok: true, data: [] };

    const postingChunks = [];
    for (let i = 0; i < ids.length; i += BILLING_POSTINGS_IN_CHUNK) {
      postingChunks.push(ids.slice(i, i + BILLING_POSTINGS_IN_CHUNK));
    }

    const chunkLists = await Promise.all(
      postingChunks.map(async (chunk) => {
        const { data: postings, error: pErr } = await supabase
          .from("trainerstunden_billing_correction_postings")
          .select("*")
          .in("statement_id", chunk)
          .order("created_at", { ascending: true });
        if (pErr) {
          console.warn(
            "[loadBillingCorrectionPostingsForStatements] Postings error:",
            pErr,
          );
          return { ok: false, error: pErr, rows: [] };
        }
        return { ok: true, rows: Array.isArray(postings) ? postings : [] };
      }),
    );

    const firstErr = chunkLists.find((x) => !x.ok);
    if (firstErr && !firstErr.ok) {
      return { ok: false, error: firstErr.error };
    }

    const list = chunkLists.flatMap((x) => x.rows || []);
    list.sort((a, b) => {
      const ta = String(a?.created_at || "");
      const tb = String(b?.created_at || "");
      if (ta !== tb) return ta.localeCompare(tb);
      return String(a?.id || "").localeCompare(String(b?.id || ""));
    });

    const correctionIds = [
      ...new Set(
        list.map((p) => String(p?.correction_id || "").trim()).filter(Boolean),
      ),
    ];
    if (!correctionIds.length) {
      return { ok: true, data: list };
    }

    const corrChunks = [];
    for (let i = 0; i < correctionIds.length; i += BILLING_POSTINGS_IN_CHUNK) {
      corrChunks.push(correctionIds.slice(i, i + BILLING_POSTINGS_IN_CHUNK));
    }

    const corrRowsNested = await Promise.all(
      corrChunks.map(async (chunk) => {
        const { data: corrections, error: cErr } = await supabase
          .from("trainerstunden_billing_corrections")
          .select("id,status,reason_code,session_id,booked_at,booked_by")
          .in("id", chunk);
        if (cErr) {
          console.warn(
            "[loadBillingCorrectionPostingsForStatements] Corrections error (Postings ohne Anreicherung):",
            cErr,
          );
          return [];
        }
        return Array.isArray(corrections) ? corrections : [];
      }),
    );

    const corrById = new Map(
      corrRowsNested.flat().map((c) => [String(c?.id || "").trim(), c]),
    );
    const enriched = list.map((p) => {
      const cid = String(p?.correction_id || "").trim();
      const c = cid ? corrById.get(cid) : null;
      return {
        ...p,
        correction_status: c?.status != null ? String(c.status) : null,
        correction_reason_code:
          c?.reason_code != null ? String(c.reason_code) : null,
        correction_session_id:
          c?.session_id != null ? String(c.session_id) : null,
        correction_booked_at: c?.booked_at != null ? c.booked_at : null,
        correction_booked_by: c?.booked_by != null ? String(c.booked_by) : null,
      };
    });
    return { ok: true, data: enriched };
  } catch (e) {
    console.error("[loadBillingCorrectionPostingsForStatements] Exception:", e);
    return { ok: false, error: e };
  }
}

/**
 * Liest gebuchte Korrektur-Postings für genau eine Rechnung (statement_id).
 * Leere ID → { ok: true, data: [] }.
 *
 * @param {string} statementId
 * @returns {Promise<{ ok: boolean, data?: object[], error?: unknown }>}
 */
export async function listBillingCorrectionPostingsForStatement(statementId) {
  const sid = String(statementId ?? "").trim();
  if (!sid) return { ok: true, data: [] };
  return loadBillingCorrectionPostingsForStatements([sid]);
}

/**
 * Aggregierte Korrektur-Postings für eine Rechnung (Korrektur-Header status booked oder reversed).
 *
 * @param {string} statementId
 * @returns {Promise<
 *   | { ok: true, statementId: string, correctionAmount: number, postingCount: number, byKind: object }
 *   | { ok: false, error: "missing-id" | "no-supabase" | "db-error", details?: unknown }
 * >}
 */
export async function getBillingStatementCorrectionSummary(statementId) {
  const sid = String(statementId ?? "").trim();
  if (!sid) return { ok: false, error: "missing-id" };
  try {
    const supabase = await getSupabaseClient();
    if (!supabase) return { ok: false, error: "no-supabase" };

    const { data: postings, error: pErr } = await supabase
      .from("trainerstunden_billing_correction_postings")
      .select("amount_eur, posting_kind, correction_id, statement_id")
      .eq("statement_id", sid);
    if (pErr) {
      console.warn("[getBillingStatementCorrectionSummary] Postings:", pErr);
      return { ok: false, error: "db-error", details: pErr };
    }

    const list = Array.isArray(postings) ? postings : [];
    const correctionIds = [
      ...new Set(
        list.map((p) => String(p?.correction_id || "").trim()).filter(Boolean),
      ),
    ];
    if (!correctionIds.length) {
      return {
        ok: true,
        statementId: sid,
        correctionAmount: 0,
        postingCount: 0,
        byKind: { delta_item: 0, adjustment_item: 0, reversal_item: 0 },
      };
    }

    const { data: corrections, error: cErr } = await supabase
      .from("trainerstunden_billing_corrections")
      .select("id,status")
      .in("id", correctionIds);
    if (cErr) {
      console.warn("[getBillingStatementCorrectionSummary] Corrections:", cErr);
      return { ok: false, error: "db-error", details: cErr };
    }

    const statusById = new Map(
      (Array.isArray(corrections) ? corrections : []).map((c) => [
        String(c?.id || "").trim(),
        String(c?.status || "").trim(),
      ]),
    );
    const enriched = list.map((p) => ({
      ...p,
      correction_status:
        statusById.get(String(p?.correction_id || "").trim()) || null,
    }));
    const agg = aggregateBookedCorrectionPostingRows(enriched);
    return { ok: true, statementId: sid, ...agg };
  } catch (e) {
    console.error("[getBillingStatementCorrectionSummary] Exception:", e);
    return { ok: false, error: "db-error", details: e };
  }
}

function __recipientCarriedKey(st) {
  const row = st && typeof st === "object" ? st : {};
  const rt =
    String(row.recipient_type || "").trim() === "contact"
      ? "contact"
      : "person";
  if (rt === "contact") {
    const bid = String(row.billing_contact_id || "").trim();
    return bid ? `carry|contact||${bid}` : "";
  }
  const pid = String(row.person_id || "").trim();
  return pid ? `carry|person|${pid}|` : "";
}

async function __loadMergeRecipientMatchContext(supabase, rows) {
  const list = Array.isArray(rows) ? rows : [];
  const createdBy = String(list[0]?.created_by || "").trim();
  if (!createdBy) {
    return { billingContacts: [], people: [] };
  }

  const [bcRes, cpRes] = await Promise.all([
    supabase
      .from("trainerstunden_billing_contacts")
      .select("id,first_name,last_name,email,phone")
      .eq("created_by", createdBy),
    supabase
      .from("club_people")
      .select(
        "id,billing_contact_id,invoice_recipient_type,email,phone,first_name,last_name",
      )
      .eq("created_by", createdBy),
  ]);

  if (bcRes.error) return { error: bcRes.error };
  if (cpRes.error) return { error: cpRes.error };

  return {
    billingContacts: bcRes.data || [],
    people: cpRes.data || [],
  };
}

function __statementsShareMergeRecipient(target, source, matchContext) {
  if (
    billingRecipientsShareCarriedGroup(
      target,
      source,
      matchContext.billingContacts,
      matchContext.people,
    )
  ) {
    return true;
  }
  const targetKey = __recipientCarriedKey(target);
  const sourceKey = __recipientCarriedKey(source);
  return Boolean(targetKey && sourceKey && targetKey === sourceKey);
}

function __sessionPersonKey(item) {
  const row = item && typeof item === "object" ? item : {};
  if (String(row.kind || "").trim() !== "session") return "";
  const sessionId = String(row.session_id || "").trim();
  const personId = String(row.person_id || "").trim();
  return sessionId && personId ? `${sessionId}|${personId}` : "";
}

function __pickMergeAnchorStatement(rows, items) {
  const list = Array.isArray(rows) ? rows : [];
  if (!list.length) return null;
  const itemRows = Array.isArray(items) ? items : [];
  const countByStatement = new Map();
  for (const it of itemRows) {
    const sid = String(it?.statement_id || "").trim();
    if (!sid) continue;
    countByStatement.set(sid, (countByStatement.get(sid) || 0) + 1);
  }

  const ranked = list
    .map((st) => ({
      st,
      itemCount: countByStatement.get(String(st?.id || "").trim()) || 0,
      periodMonth: String(st?.period_month || "").trim(),
    }))
    .sort((a, b) => {
      if (b.itemCount !== a.itemCount) return b.itemCount - a.itemCount;
      if (a.periodMonth && b.periodMonth) {
        return a.periodMonth.localeCompare(b.periodMonth);
      }
      return 0;
    });

  return ranked[0]?.st || list[0];
}

async function __mergeDraftSourceIntoTarget(
  supabase,
  targetId,
  sourceId,
  sourceCreatedBy,
) {
  const { data: sourceItems, error: selErr } = await supabase
    .from("trainerstunden_billing_statement_items")
    .select("id,session_id,person_id,kind,amount_eur,created_by")
    .eq("statement_id", sourceId);
  if (selErr) return { ok: false, error: selErr };

  const { data: targetItems, error: tgtErr } = await supabase
    .from("trainerstunden_billing_statement_items")
    .select("id,session_id,person_id,kind,amount_eur")
    .eq("statement_id", targetId);
  if (tgtErr) return { ok: false, error: tgtErr };

  const targetSessionAmounts = new Map();
  for (const it of Array.isArray(targetItems) ? targetItems : []) {
    const key = __sessionPersonKey(it);
    if (key) targetSessionAmounts.set(key, Number(it?.amount_eur) || 0);
  }

  let moved = 0;
  for (const item of Array.isArray(sourceItems) ? sourceItems : []) {
    const itemId = String(item?.id || "").trim();
    if (!itemId) continue;

    if (!String(item?.created_by || "").trim() && sourceCreatedBy) {
      const { error: ownerErr } = await supabase
        .from("trainerstunden_billing_statement_items")
        .update({ created_by: sourceCreatedBy })
        .eq("id", itemId);
      if (ownerErr) return { ok: false, error: ownerErr };
    }

    const key = __sessionPersonKey(item);
    if (key && targetSessionAmounts.has(key)) {
      const existingAmount = targetSessionAmounts.get(key) || 0;
      const itemAmount = Number(item?.amount_eur) || 0;
      if (Math.abs(existingAmount - itemAmount) < 0.005) {
        const { error: delErr } = await supabase
          .from("trainerstunden_billing_statement_items")
          .delete()
          .eq("id", itemId);
        if (delErr) return { ok: false, error: delErr };
        moved += 1;
        continue;
      }
      return { ok: false, error: "duplicate-session-person-active" };
    }

    const { error: moveErr } = await supabase
      .from("trainerstunden_billing_statement_items")
      .update({ statement_id: targetId })
      .eq("id", itemId);
    if (moveErr) {
      if (isTrainerstundenBillingDuplicateSessionPersonError(moveErr)) {
        return {
          ok: false,
          error: "duplicate-session-person-active",
          details: moveErr,
        };
      }
      return { ok: false, error: moveErr };
    }
    moved += 1;
    if (key) targetSessionAmounts.set(key, Number(item?.amount_eur) || 0);
  }

  return { ok: true, moved };
}

/**
 * Führt mehrere Entwürfe desselben Empfängers zu einer Gesamtrechnung zusammen (eine Rechnungsnummer / ein Zahlungslink).
 *
 * @param {string} targetStatementId
 * @param {string[]} sourceStatementIds
 */
export async function mergeBillingDraftStatements(
  targetStatementId,
  sourceStatementIds,
  options = {},
) {
  try {
    const allowSplinters = options?.allowSplinters === true;
    const supabase = await getSupabaseClient();
    if (!supabase) return { ok: false, error: "no-supabase" };

    let targetId = String(targetStatementId || "").trim();
    const explicitIds = [
      ...new Set(
        (Array.isArray(options?.allStatementIds) ? options.allStatementIds : [])
          .map((id) => String(id || "").trim())
          .filter(Boolean),
      ),
    ];
    let sourceIds = [
      ...new Set(
        (Array.isArray(sourceStatementIds) ? sourceStatementIds : [])
          .map((id) => String(id || "").trim())
          .filter(Boolean)
          .filter((id) => id !== targetId),
      ),
    ];

    let allIds = targetId
      ? [targetId, ...sourceIds]
      : explicitIds.length >= 2
        ? explicitIds
        : [...sourceIds];
    allIds = [...new Set(allIds.filter(Boolean))];
    if (allIds.length < 2) {
      return { ok: false, error: "need-multiple" };
    }

    const { data: statements, error: stErr } = await supabase
      .from("trainerstunden_billing_statements")
      .select(
        "id,status,recipient_type,person_id,billing_contact_id,total_eur,open_eur,payment_reference,draft_issuer_trainer_id,draft_merge_blocked,split_from_statement_id,period_month,created_by",
      )
      .in("id", allIds);
    if (stErr) return { ok: false, error: stErr };

    const rows = Array.isArray(statements) ? statements : [];
    if (rows.length !== allIds.length) {
      return { ok: false, error: "statement-not-found" };
    }

    const { data: allItems, error: allItemsErr } = await supabase
      .from("trainerstunden_billing_statement_items")
      .select("id,statement_id")
      .in("statement_id", allIds);
    if (allItemsErr) return { ok: false, error: allItemsErr };

    if (!targetId) {
      const anchor = __pickMergeAnchorStatement(
        rows,
        Array.isArray(allItems) ? allItems : [],
      );
      targetId = String(anchor?.id || "").trim();
      if (!targetId) return { ok: false, error: "missing-target" };
      sourceIds = allIds.filter((id) => id !== targetId);
    } else if (!sourceIds.length) {
      sourceIds = allIds.filter((id) => id !== targetId);
    }
    if (!sourceIds.length) {
      return { ok: false, error: "need-multiple" };
    }

    for (const row of rows) {
      if (!allowSplinters && isBillingStatementDraftMergeBlocked(row)) {
        return { ok: false, error: "merge-blocked-splinter" };
      }
    }

    const target = rows.find((r) => String(r?.id || "") === targetId);
    if (!target || String(target.status || "").trim() !== "draft") {
      return { ok: false, error: "not-draft" };
    }

    const matchContext = await __loadMergeRecipientMatchContext(supabase, rows);
    if (matchContext.error) return { ok: false, error: matchContext.error };

    const hasTargetRecipient =
      String(target.billing_contact_id || "").trim() ||
      String(target.person_id || "").trim();
    if (!hasTargetRecipient) {
      return { ok: false, error: "invalid-recipient" };
    }

    for (const source of rows.filter((r) => String(r?.id || "") !== targetId)) {
      if (String(source?.status || "").trim() !== "draft") {
        return { ok: false, error: "not-draft" };
      }
      if (!__statementsShareMergeRecipient(target, source, matchContext)) {
        return { ok: false, error: "recipient-mismatch" };
      }
      if (
        !allowSplinters &&
        areBillingStatementsInSameSplitTree(
          targetId,
          String(source?.id || ""),
          rows,
        )
      ) {
        return { ok: false, error: "split-tree-conflict" };
      }
    }

    let mergedItemCount = 0;
    for (const sourceId of sourceIds) {
      const source = rows.find((r) => String(r?.id || "") === sourceId);
      const moveResult = await __mergeDraftSourceIntoTarget(
        supabase,
        targetId,
        sourceId,
        String(source?.created_by || "").trim(),
      );
      if (!moveResult.ok) return moveResult;
      mergedItemCount += Number(moveResult.moved) || 0;

      const { error: delErr } = await supabase
        .from("trainerstunden_billing_statements")
        .delete()
        .eq("id", sourceId)
        .eq("status", "draft");
      if (delErr) return { ok: false, error: delErr };
    }

    const { data: targetItems, error: itemsErr } = await supabase
      .from("trainerstunden_billing_statement_items")
      .select("amount_eur")
      .eq("statement_id", targetId);
    if (itemsErr) return { ok: false, error: itemsErr };

    const newTotal = round2(
      (Array.isArray(targetItems) ? targetItems : []).reduce(
        (sum, it) => sum + (Number(it?.amount_eur) || 0),
        0,
      ),
    );

    const explicitIssuer = String(options?.draftIssuerTrainerId ?? "").trim();
    const preservedIssuer =
      explicitIssuer ||
      String(target?.draft_issuer_trainer_id ?? "").trim() ||
      null;

    const { data: updated, error: uErr } = await supabase
      .from("trainerstunden_billing_statements")
      .update({
        total_eur: newTotal,
        open_eur: newTotal,
        payment_reference: null,
        draft_issuer_trainer_id: preservedIssuer,
        ...(allowSplinters
          ? { draft_merge_blocked: false, split_from_statement_id: null }
          : {}),
      })
      .eq("id", targetId)
      .eq("status", "draft")
      .select()
      .single();
    if (uErr) return { ok: false, error: uErr };

    const snap = await snapshotBillingStatementDraftMasterData(
      supabase,
      targetId,
    );
    if (!snap.ok) {
      console.warn(
        "[mergeBillingDraftStatements] draft master-data snapshot failed:",
        snap.reason || snap.error,
      );
    }

    return {
      ok: true,
      targetStatementId: targetId,
      mergedStatementCount: sourceIds.length,
      mergedItemCount,
      data: updated,
    };
  } catch (e) {
    console.error("[mergeBillingDraftStatements] Exception:", e);
    return { ok: false, error: e };
  }
}

/**
 * Rechnungssteller für Entwürfe manuell setzen (z. B. vor Versand / in der Vorschau).
 *
 * @param {string[]|string} statementIds
 * @param {string|null|undefined} draftIssuerTrainerId encoded choice oder Trainer-ID
 */
export async function updateBillingStatementDraftIssuer(
  statementIds,
  draftIssuerTrainerId,
) {
  try {
    const supabase = await getSupabaseClient();
    if (!supabase) return { ok: false, error: "no-supabase" };

    const ids = (Array.isArray(statementIds) ? statementIds : [statementIds])
      .map((id) => String(id ?? "").trim())
      .filter(Boolean);
    if (!ids.length) return { ok: false, error: "missing-ids" };

    const val = String(draftIssuerTrainerId ?? "").trim() || null;
    const updated = [];

    for (const id of ids) {
      const { data, error } = await supabase
        .from("trainerstunden_billing_statements")
        .update({ draft_issuer_trainer_id: val })
        .eq("id", id)
        .eq("status", "draft")
        .select()
        .single();
      if (error) return { ok: false, error, statementId: id };
      if (data) updated.push(data);
    }

    return { ok: true, data: updated };
  } catch (e) {
    console.error("[updateBillingStatementDraftIssuer] Exception:", e);
    return { ok: false, error: e };
  }
}
